import { Controller } from "@hotwired/stimulus";
import type { Chart } from "chart.js";

import { renderLegend, renderTooltip } from "~/lib/chart-dom";
import type { Chart as ChartSpec } from "~/lib/chart-spec";
import { buildConfig, type ChartKind, readTheme } from "~/lib/charts";
import logger from "~/lib/logger";

const log = logger();

export default class ChartController extends Controller<HTMLElement> {
  static override targets = ["canvas", "legend", "spec"];
  static override values = { totalLabel: { type: String, default: "Total" } };

  declare readonly canvasTarget: HTMLCanvasElement;
  declare readonly hasLegendTarget: boolean;
  declare readonly legendTarget: HTMLElement;
  declare readonly specTarget: HTMLScriptElement;
  declare readonly totalLabelValue: string;

  private chart: Chart<ChartKind> | null = null;

  override connect(): void {
    this.render().catch((reason: unknown) => {
      log.error("Failed to render chart:", reason);
    });
  }

  override disconnect(): void {
    this.chart?.destroy();
    this.chart = null;
  }

  private async render(): Promise<void> {
    const spec = JSON.parse(this.specTarget.textContent ?? "") as ChartSpec;
    const {
      Chart,
      BarController,
      BarElement,
      LineController,
      LineElement,
      PointElement,
      CategoryScale,
      LinearScale,
      Filler,
      Tooltip,
      Legend,
    } = await import("chart.js");
    Chart.register(
      BarController,
      BarElement,
      LineController,
      LineElement,
      PointElement,
      CategoryScale,
      LinearScale,
      Filler,
      Tooltip,
      Legend,
    );
    // Chart.js draws canvas text with the fonts that are loaded at that time.
    // Without this, the first draw uses a fallback font.
    await document.fonts.ready;

    if (!this.element.isConnected || this.chart !== null) return;
    const theme = readTheme(this.element);
    const chart = new Chart(
      this.canvasTarget,
      buildConfig(
        spec,
        theme,
        (context) => {
          renderTooltip(context, spec, theme, this.totalLabelValue);
        },
        (url) => {
          if (!this.dispatchOpen(url)) window.location.assign(url);
        },
      ),
    );
    this.chart = chart;
    if (this.hasLegendTarget && spec.show_legend && !spec.is_compact) {
      renderLegend(this.legendTarget, spec, theme);
    }
  }

  /**
   * Send a click on a legend link as a `chart:open` event. It runs in the
   * capture phase, so that a listener that cancels the event also stops
   * htmx from boosting the link.
   */
  openLegendLink(event: MouseEvent): void {
    const link = (event.target as Element).closest("a");
    if (link === null || event.metaKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    if (this.dispatchOpen(link.href)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Send a cancelable `chart:open` event with the URL that a click opens.
   * A listener that cancels it opens the URL in its own way. Returns true
   * when a listener canceled the event.
   */
  private dispatchOpen(url: string): boolean {
    return this.dispatch("open", { detail: { url }, cancelable: true })
      .defaultPrevented;
  }
}
